import { captureMessage } from '@sentry/browser';
import { nanoid } from 'nanoid';
import { showNotification } from 'platform/components';
import { isNil, map, move, pipe } from 'ramda';
import { isNilOrEmpty } from 'ramda-adjunct';
import { useHttpCalls } from '../api/useHttpCalls';
import { selectActivePreset, selectActivePresetDataQuery, selectCustomPresets, selectGridCode, selectIsPresetChanged } from '../store/createEagGridStore';
import { useEagGridSelector } from '../store/useEagGridSelector';
import { useEagGridStore } from '../store/useEagGridStore';
import { ActivePreset, ColumnsSetting, FeGridSettings, GetDataQueryResponse, Preset, PresetDetail, PostPresetRequest, PutPresetRenameRequest, PutPresetRequest, VirtualPreset } from '../types/Api';
import { filterTechnicalArray } from '../utils/filterTechnicalArray';
import { isPresetColumnsSettingsInvalid } from '../utils/isPresetColumnsSettingsInvalid';
import { isPresetCustom } from '../utils/isPresetCustom';
import { getSearchParamsByGridCode } from '../utils/url/getSearchParamsByGridCode';
import { setSearchParamsByGridCode } from '../utils/url/setSearchParamsByGridCode';
export type UsePresetStateReturn = {
  presets: Preset[];
  activePreset: ActivePreset;
  activePresetDataQuery: GetDataQueryResponse | null;
  setActivePreset: (preset: ActivePreset) => void;
  activatePreset: (preset: Preset) => Promise<void>;
  deactivatePreset: () => Promise<void>;
  createPreset: (title: string, description: string, currentColumnSettings: ColumnsSetting[], currentSettings: FeGridSettings) => Promise<void>;
  updatePreset: (presetId: string, currentColumnSettings: ColumnsSetting[], currentSettings: FeGridSettings) => Promise<void>;
  deletePreset: (presetId: string) => Promise<void>;
  duplicatePreset: (presetId: string) => Promise<void>;
  renamePreset: (presetId: string, title: string, description: string) => Promise<void>;
  updatePresetPosition: (fromIdx: number, toIdx: number) => Promise<void>;
  updateVirtualPreset: (virtualPreset: VirtualPreset) => Promise<void>;
  isPresetChanged: boolean;
  setIsPresetChanged: (changed: boolean) => void;
  resetOverrides: () => Promise<void>;
  rowCountGetter: (dataQueryId: string) => Promise<number>;
};
export const usePresetState = (): UsePresetStateReturn => {
  const httpCalls = useHttpCalls();
  const store = useEagGridStore();
  const gridCode = useEagGridSelector(selectGridCode);
  const activePreset = useEagGridSelector(selectActivePreset)!;
  const customPresets = useEagGridSelector(selectCustomPresets) ?? [];
  const activePresetDataQuery = useEagGridSelector(selectActivePresetDataQuery) ?? null;
  const isPresetChanged = useEagGridSelector(selectIsPresetChanged);
  const refreshPresets = () => httpCalls.getPresets().then(response => {
    if (response) {
      store.setCustomPresets(response);
    }
    return response;
  });
  const activatePreset = async (preset: Preset): Promise<void> => {
    const presetDetail = await httpCalls.getPreset(preset.id);
    const dataQuery = await httpCalls.getDataQuery(preset.dataQueryId);
    if (!presetDetail || !dataQuery) {
      return;
    }
    setSearchParamsByGridCode(gridCode, preset.dataQueryId);
    store.setActivePreset(presetDetail);
    store.setActivePresetDataQuery(dataQuery);
    await httpCalls.activatePreset(preset.id).then(() => {
      store.setDataQuery(dataQuery);
      store.setActivePreset(presetDetail);
      setSearchParamsByGridCode(gridCode, preset.dataQueryId);
      store.setIsPresetChanged(false);
    });
  };
  const deactivatePreset = async (): Promise<void> => {
    const newVirtualPreset = await httpCalls.getVirtualPreset();
    if (!newVirtualPreset) {
      return;
    }
    const dataQuery = await httpCalls.getDataQuery(newVirtualPreset.dataQueryId);
    if (!dataQuery) {
      return;
    }
    setSearchParamsByGridCode(gridCode, newVirtualPreset.dataQueryId);

    // BeVirtualPreset and VirtualPreset have the same runtime shape — the only
    // difference is BeGridSettings vs FeGridSettings at the type level, which
    // are structurally identical. We reconstruct the object explicitly to
    // satisfy the type without using a double assertion.
    const virtualPresetAsActive: VirtualPreset = {
      columnsSettings: newVirtualPreset.columnsSettings,
      dataQueryId: newVirtualPreset.dataQueryId,
      gridSettings: newVirtualPreset.gridSettings as VirtualPreset['gridSettings']
    };
    store.setActivePreset(virtualPresetAsActive);
    store.setActivePresetDataQuery(dataQuery);
    await httpCalls.activatePreset(null).then(() => {
      store.setDataQuery(dataQuery);
      store.setActivePreset(virtualPresetAsActive);
      setSearchParamsByGridCode(gridCode, newVirtualPreset.dataQueryId);
      store.setActivePresetDataQuery(dataQuery);
      store.setIsPresetChanged(false);
    });
  };
  const createPreset = async (title: string, description: string, currentColumnSettings: ColumnsSetting[], currentSettings: FeGridSettings): Promise<void> => {
    const dataQueryId = getSearchParamsByGridCode(gridCode)?.queryId;
    const preset: PostPresetRequest = {
      id: nanoid(),
      title,
      description,
      dataQueryId: dataQueryId ?? '',
      gridSettings: currentSettings,
      columnsSettings: currentColumnSettings,
      isActive: true
    };
    await httpCalls.createPreset(preset).then(async newPreset => {
      const response = await httpCalls.getPresets();
      if (!response) {
        return;
      }
      store.setCustomPresets(response);
      const createdPreset = response.find(res => res.id === newPreset?.presetId);
      if (isNil(createdPreset)) {
        showNotification.error('Preset was not found. Unable to activate');
        captureMessage('createPreset', scope => scope.setLevel('error').setExtras({
          userDescription: 'create custom preset from usePresetState - createPreset',
          state: createdPreset
        }));
        return;
      }
      await activatePreset(createdPreset!);
    });
  };
  const updatePreset = async (presetId: string, currentColumnSettings: ColumnsSetting[], currentSettings: FeGridSettings): Promise<void> => {
    const modifiedPreset: PutPresetRequest = {
      gridSettings: currentSettings,
      columnsSettings: currentColumnSettings,
      dataQueryId: getSearchParamsByGridCode(gridCode)?.queryId ?? ''
    };
    if (isPresetColumnsSettingsInvalid(modifiedPreset)) {
      captureMessage('updatePreset', scope => scope.setLevel('error').setExtras({
        userDescription: 'update custom preset from usePresetState - updatePreset',
        state: modifiedPreset
      }));
      return;
    }
    await httpCalls.updatePreset(presetId, modifiedPreset).then(() => {
      const prev = store.getState().activePreset;
      store.setActivePreset({
        ...(prev ?? {}),
        ...modifiedPreset
      } as ActivePreset);
      store.setIsPresetChanged(false);
    });
    await refreshPresets();
  };
  const deletePreset = async (presetId: string): Promise<void> => {
    const shouldDeactivatePreset = isPresetCustom(activePreset) && activePreset.id === presetId;
    await httpCalls.deletePreset(presetId);
    if (shouldDeactivatePreset) {
      await deactivatePreset();
    }
    await refreshPresets();
  };
  const duplicatePreset = async (presetId: string): Promise<void> => {
    const presetDetail = await httpCalls.getPreset(presetId);
    if (!presetDetail) {
      return;
    }
    const preset: PresetDetail = {
      ...presetDetail,
      id: nanoid(),
      title: `${presetDetail.title} (copy)`
    };
    await httpCalls.createPreset(preset);
    await refreshPresets();
  };
  const renamePreset = async (presetId: string, title: string, description: string): Promise<void> => {
    const renamedPreset: PutPresetRenameRequest = {
      title,
      description
    };
    await httpCalls.updateRenamePreset(presetId, renamedPreset);
    await refreshPresets();
  };
  const updatePresetPosition = async (fromIdx: number, toIdx: number): Promise<void> => {
    const newCustomPresetsOrder = pipe<[Preset[]], string[], string[]>(map(preset => preset.id), move(fromIdx)(toIdx))(customPresets);
    await httpCalls.presetOrder(newCustomPresetsOrder);
    await refreshPresets();
  };
  const updateVirtualPreset = async (virtualPreset: VirtualPreset): Promise<void> => {
    const searchParams = getSearchParamsByGridCode(gridCode);
    if (isNilOrEmpty(searchParams)) {
      return;
    }
    const dataQueryId = searchParams?.queryId;
    if (isNilOrEmpty(dataQueryId)) {
      return;
    }
    const modifiedPreset: VirtualPreset = {
      ...virtualPreset,
      columnsSettings: filterTechnicalArray(virtualPreset.columnsSettings),
      dataQueryId: dataQueryId ?? ''
    };
    if (isPresetColumnsSettingsInvalid(modifiedPreset)) {
      captureMessage('updatePreset', scope => scope.setLevel('error').setExtras({
        userDescription: 'update virtual preset from usePresetState - updateVirtualPreset',
        state: modifiedPreset
      }));
      return;
    }
    await httpCalls.updateVirtualPreset(modifiedPreset);
  };
  const resetOverrides = async (): Promise<void> => {
    if (!isPresetCustom(activePreset)) {
      return;
    }
    await activatePreset(activePreset as Preset);
  };
  const rowCountGetter = (dataQueryId: string): Promise<number> => httpCalls.getCountByQuery(dataQueryId).then(response => response ?? 0);
  return {
    presets: customPresets,
    activePreset,
    activePresetDataQuery,
    setActivePreset: store.setActivePreset,
    activatePreset,
    deactivatePreset,
    createPreset,
    updatePreset,
    deletePreset,
    duplicatePreset,
    renamePreset,
    updatePresetPosition,
    updateVirtualPreset,
    isPresetChanged,
    setIsPresetChanged: store.setIsPresetChanged,
    resetOverrides,
    rowCountGetter
  };
};